// Tiny DOM + fetch helpers shared by all client pages.
import { getLang, setLang, type Lang } from "./i18n.ts";

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> & { class?: string } = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (k === "class") node.className = v as string;
    else if (k.startsWith("on") && typeof v === "function")
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (k === "style" && typeof v === "object") Object.assign(node.style, v);
    else (node as any)[k] = v;
  }
  for (const c of children) node.append(c);
  return node;
}

export function $<T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document): T | null {
  return root.querySelector<T>(sel);
}

export async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      msg = (await res.json()).error || msg;
    } catch {}
    throw new Error(msg);
  }
  const ct = res.headers.get("content-type") || "";
  return (ct.includes("json") ? await res.json() : ((await res.text()) as any)) as T;
}

// Fixed language toggle (EN / LV) in the top-right corner of every page.
// Switching persists the choice and reloads so the whole page re-renders in the
// new language (mid-game players simply reconnect via their rejoin token).
export function mountLangToggle() {
  const cur = getLang();
  const wrap = el("div", { class: "lang-toggle" });
  (["lv", "en"] as Lang[]).forEach((l) => {
    const btn = el("button", { class: "lang-btn" + (cur === l ? " active" : "") }, l.toUpperCase());
    btn.addEventListener("click", () => {
      if (l === getLang()) return;
      setLang(l);
      location.reload();
    });
    wrap.append(btn);
  });
  document.body.append(wrap);
}

// Decorate a page with floating pastel background blobs.
export function addBlobs(colors = ["#bfe1fb", "#c4e8ff", "#d8c4ff", "#c9f5df", "#cfeeff"]) {
  const wrap = el("div", { class: "blobs" });
  const spots = [
    [8, 12, 260],
    [78, 8, 220],
    [12, 72, 240],
    [82, 66, 200],
    [46, 40, 180],
  ];
  spots.forEach(([x, y, s], i) => {
    wrap.append(
      el("div", {
        class: "blob",
        style: {
          left: x + "%",
          top: y + "%",
          width: s + "px",
          height: s + "px",
          background: colors[i % colors.length],
          animationDelay: i * 1.3 + "s",
        } as any,
      }),
    );
  });
  document.body.prepend(wrap);
}
